import { DEAL_OPERATIONS, type DealOperation } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowDown, ArrowUp, Plus } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Field, Input, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import {
  dealKeys,
  useLostReasons,
  usePipelines,
  type Pipeline,
  type Stage,
} from '../../lib/deals-api.ts';
import { useToast } from '../../lib/toast.tsx';

/** Settings of the sales process (HU-CRM-08, HU-CRM-10). */
export function PipelinesPage() {
  const pipelines = usePipelines(true);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Embudos y etapas</h1>
        <p className="text-sm text-muted-foreground">
          Las etapas por las que pasa cada negocio, su probabilidad de cierre y cuántos días sin
          actividad lo marcan como estancado.
        </p>
      </div>
      {pipelines.isPending ? (
        <Loading />
      ) : pipelines.error ? (
        <Alert tone="danger">{errorMessage(pipelines.error)}</Alert>
      ) : (
        <>
          {pipelines.data.map((p) => (
            <PipelineCard key={p.id} pipeline={p} />
          ))}
          <NewPipeline />
        </>
      )}
      <LostReasonsCard />
    </div>
  );
}

function useSave() {
  const client = useQueryClient();
  const notify = useToast();
  return async (path: string, method: 'POST' | 'PATCH', body: unknown, done?: string) => {
    try {
      await api(path, { method, body });
      await client.invalidateQueries({ queryKey: dealKeys.pipelines });
      await client.invalidateQueries({ queryKey: dealKeys.lostReasons });
      if (done) notify(done, 'success');
      return true;
    } catch (error) {
      notify(errorMessage(error), 'error');
      return false;
    }
  };
}

function PipelineCard({ pipeline }: { pipeline: Pipeline }) {
  const save = useSave();
  const [name, setName] = useState(pipeline.name);
  const active = pipeline.stages.filter((s) => !s.archived);
  const move = (index: number, delta: number) => {
    const ids = active.map((s) => s.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    ids.splice(target, 0, ...ids.splice(index, 1));
    void save(`/pipelines/${pipeline.id}/stages/order`, 'POST', { ids });
  };
  return (
    <Card className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Field label="Nombre del embudo">
            {(a11y) => (
              <Input
                {...a11y}
                value={name}
                maxLength={60}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge>{DEAL_OPERATIONS[pipeline.operation]}</Badge>
          {pipeline.isDefault && <Badge tone="info">Predeterminado</Badge>}
          {pipeline.archived && <Badge>Archivado</Badge>}
          {name.trim() !== pipeline.name && (
            <Button
              size="sm"
              onClick={() =>
                void save(`/pipelines/${pipeline.id}`, 'PATCH', { name }, 'Nombre guardado.')
              }
            >
              Guardar nombre
            </Button>
          )}
          {!pipeline.isDefault && !pipeline.archived && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                void save(
                  `/pipelines/${pipeline.id}`,
                  'PATCH',
                  { isDefault: true },
                  'Ahora es el predeterminado.',
                )
              }
            >
              Hacer predeterminado
            </Button>
          )}
          {!pipeline.isDefault && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                void save(`/pipelines/${pipeline.id}`, 'PATCH', { archived: !pipeline.archived })
              }
            >
              <Archive /> {pipeline.archived ? 'Restaurar' : 'Archivar'}
            </Button>
          )}
        </div>
      </div>
      {!pipeline.archived && (
        <>
          <ul className="space-y-2">
            {active.map((stage, index) => (
              <StageRow
                key={stage.id}
                pipelineId={pipeline.id}
                stage={stage}
                first={index === 0}
                last={index === active.length - 1}
                onMove={(delta) => {
                  move(index, delta);
                }}
              />
            ))}
          </ul>
          <NewStage pipelineId={pipeline.id} />
        </>
      )}
    </Card>
  );
}

function StageRow({
  pipelineId,
  stage,
  first,
  last,
  onMove,
}: {
  pipelineId: string;
  stage: Stage;
  first: boolean;
  last: boolean;
  onMove: (delta: number) => void;
}) {
  const save = useSave();
  const [name, setName] = useState(stage.name);
  const [probability, setProbability] = useState(String(stage.probability));
  const [staleDays, setStaleDays] = useState(
    stage.staleDays === null ? '' : String(stage.staleDays),
  );
  const changed =
    name.trim() !== stage.name ||
    Number(probability) !== stage.probability ||
    (staleDays === '' ? null : Number(staleDays)) !== stage.staleDays;
  const path = `/pipelines/${pipelineId}/stages/${stage.id}`;
  return (
    <li className="grid items-end gap-2 rounded-lg border border-border p-2 sm:grid-cols-[1fr_7rem_8rem_auto]">
      <Field label="Etapa">
        {(a11y) => (
          <Input
            {...a11y}
            maxLength={60}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        )}
      </Field>
      <Field label="Probabilidad (%)">
        {(a11y) => (
          <Input
            {...a11y}
            type="number"
            min={0}
            max={100}
            value={probability}
            onChange={(e) => {
              setProbability(e.target.value);
            }}
          />
        )}
      </Field>
      <Field label="Estancado a los (días)">
        {(a11y) => (
          <Input
            {...a11y}
            type="number"
            min={1}
            max={365}
            placeholder="Nunca"
            value={staleDays}
            onChange={(e) => {
              setStaleDays(e.target.value);
            }}
          />
        )}
      </Field>
      <div className="flex items-center gap-0.5">
        {changed && (
          <Button
            size="sm"
            onClick={() =>
              void save(
                path,
                'PATCH',
                {
                  name,
                  probability: Number(probability),
                  staleDays: staleDays === '' ? null : Number(staleDays),
                },
                'Etapa guardada.',
              )
            }
          >
            Guardar
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          aria-label={`Subir ${stage.name}`}
          disabled={first}
          onClick={() => {
            onMove(-1);
          }}
        >
          <ArrowUp />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          aria-label={`Bajar ${stage.name}`}
          disabled={last}
          onClick={() => {
            onMove(1);
          }}
        >
          <ArrowDown />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          aria-label={`Archivar ${stage.name}`}
          onClick={() => void save(path, 'PATCH', { archived: true }, 'Etapa archivada.')}
        >
          <Archive />
        </Button>
      </div>
    </li>
  );
}

function NewStage({ pipelineId }: { pipelineId: string }) {
  const save = useSave();
  const [name, setName] = useState('');
  return (
    <form
      className="flex flex-col gap-2 sm:flex-row sm:items-end"
      onSubmit={(e) => {
        e.preventDefault();
        void save(
          `/pipelines/${pipelineId}/stages`,
          'POST',
          { name, probability: 50, staleDays: 7 },
          'Etapa agregada.',
        ).then((ok) => {
          if (ok) setName('');
        });
      }}
    >
      <div className="flex-1">
        <Field label="Nueva etapa">
          {(a11y) => (
            <Input
              {...a11y}
              maxLength={60}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          )}
        </Field>
      </div>
      <Button type="submit" variant="secondary" disabled={!name.trim()}>
        <Plus /> Agregar etapa
      </Button>
    </form>
  );
}

function NewPipeline() {
  const save = useSave();
  const [name, setName] = useState('');
  const [operation, setOperation] = useState<DealOperation>('purchase');
  return (
    <Card>
      <form
        className="flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          void save('/pipelines', 'POST', { name, operation }, 'Embudo creado.').then((ok) => {
            if (ok) setName('');
          });
        }}
      >
        <div className="flex-1">
          <Field label="Nuevo embudo">
            {(a11y) => (
              <Input
                {...a11y}
                maxLength={60}
                placeholder="Proyectos nuevos"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <Field label="Operación">
          {(a11y) => (
            <Select
              {...a11y}
              value={operation}
              onChange={(e) => {
                setOperation(e.target.value as DealOperation);
              }}
            >
              {(Object.entries(DEAL_OPERATIONS) as [DealOperation, string][]).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Button type="submit" variant="secondary" disabled={!name.trim()}>
          <Plus /> Crear embudo
        </Button>
      </form>
    </Card>
  );
}

function LostReasonsCard() {
  const reasons = useLostReasons(true);
  const save = useSave();
  const [name, setName] = useState('');
  return (
    <Card className="space-y-3">
      <div>
        <h2 className="font-semibold">Motivos de pérdida</h2>
        <p className="text-sm text-muted-foreground">
          Se elige uno al marcar un negocio como perdido. Los que ya se usaron no se borran: se
          desactivan.
        </p>
      </div>
      <ul className="divide-y divide-border text-sm">
        {(reasons.data ?? []).map((r) => (
          <li key={r.id} className="flex min-h-11 items-center justify-between gap-2">
            <span className={r.active ? '' : 'text-muted-foreground line-through'}>{r.name}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void save(`/lost-reasons/${r.id}`, 'PATCH', { active: !r.active })}
            >
              {r.active ? 'Desactivar' : 'Activar'}
            </Button>
          </li>
        ))}
      </ul>
      <form
        className="flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          void save('/lost-reasons', 'POST', { name }, 'Motivo agregado.').then((ok) => {
            if (ok) setName('');
          });
        }}
      >
        <div className="flex-1">
          <Field label="Nuevo motivo">
            {(a11y) => (
              <Input
                {...a11y}
                maxLength={80}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <Button type="submit" variant="secondary" disabled={!name.trim()}>
          <Plus /> Agregar
        </Button>
      </form>
    </Card>
  );
}
